import { useState, useRef } from "react";
import { flushSync } from "react-dom";
import PropTypes from "prop-types";
import Markdown from "./markdown";
import clsx from "clsx";
import TextareaAutosize from "react-textarea-autosize";

export default function MarkdownInput({
  className,
  value,
  setValue,
  placeholder,
  placeholderClassName,
  inputClassName,
  markdownClassName
}) {
  const [focused, setFocused] = useState(false);

  const inputRef = useRef();

  const handleClick = () => {
    if (focused) return;
    // Mount the textarea and focus it inside the tap itself: iOS only opens the keyboard for focus() called
    // synchronously from a user gesture, not from an effect that runs after it.
    flushSync(() => setFocused(true));
    const input = inputRef.current;
    input?.focus();
    input?.setSelectionRange(input.value.length, input.value.length);
  };
  const handleBlur = event => {
    // Focus moving from the wrapper into its own textarea (keyboard entry) does not leave the editor
    if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);
  };

  // Tabbable while showing the preview: keyboard focus opens the textarea just like a click
  return <div
    className={clsx(className)}
    tabIndex={focused ? undefined : 0}
    onFocus={handleClick}
    onClick={handleClick}
    onBlur={handleBlur}
  >
    {(() => {
      if (!focused && !value) {
        return <span className={clsx("markdown-input__placeholder", placeholderClassName)}>{placeholder}</span>
      } else if (focused) {
        return <TextareaAutosize
          spellCheck={false}
          className={clsx("markdown-input__input", "auto-resize", inputClassName)}
          ref={inputRef}
          placeholder={placeholder}
          value={value}
          onChange={event => setValue(event.target.value)}
        />;
      } else {
        return <Markdown className={clsx("markdown-input__markdown", markdownClassName)}>{value}</Markdown>;
      }
    })()}
  </div>;
}

MarkdownInput.propTypes = {
  className: PropTypes.string,
  value: PropTypes.string.isRequired,
  setValue: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  placeholderClassName: PropTypes.string,
  inputClassName: PropTypes.string,
  markdownClassName: PropTypes.string,
}
